import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { Field, NotificationInstance, NotificationInstanceCollection, Target } from 'src/app/common/entity/notification/notification.model';
import { TargetTypes } from 'src/app/common/entity/enum/targetTypes.enum';
import { NotificationClient } from './../../../../client/notification.client';
import { toUtcDate } from 'src/app/common/utils/utils';

@Component({
  selector: 'app-create-notification',
  templateUrl: './create-notification.component.html',
  styleUrls: ['./create-notification.component.css']
})
export class CreateNotificationComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() loadData: boolean = false;
  @Output() notificationCreated: EventEmitter<NotificationInstance> = new EventEmitter<NotificationInstance>();

  notificationMetadata: NotificationInstanceCollection = [];
  availableNotificationTypes: { value: string, label: string }[];
  availableTargets: Target[];
  availableProperties: Field[];
  availableTargetOptions: { value: string, label: string }[];
  targetRights: { CompanyGroupCode: number, CompanyGroupName: string, CustomerCode: number, CustomerName: string };

  private notificationClient = inject(NotificationClient);
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);


  userCollection: any[];
  locationCollection: any[];
  companyGroupCollection: any[];

  createNotificationForm = new FormGroup({
    notificationType: new FormControl('', Validators.required),
    targetType: new FormControl('', Validators.required),
    selectedCompanyGroup: new FormControl([]),
    selectedLocation: new FormControl([]),
    selectedUser: new FormControl([]),
    dynamicFields: this.formBuilder.group({})
  });

  dynamicFieldsMetadata: Field[] = [];

  get selectedNotificationType(): string {
    return this.createNotificationForm.controls.notificationType.value;
  }

  set selectedNotificationType(value) {
    this.createNotificationForm.controls.notificationType.setValue(value);
  }

  get selectedTargetType() {
    return this.createNotificationForm.controls.targetType.value;
  }

  set selectedTargetType(value) {
    this.createNotificationForm.controls.targetType.setValue(value);
  }

  get selectedNotificationInstance(): NotificationInstance {
    return this.notificationMetadata.find(c => c.Code == this.selectedNotificationType);
  }

  get isLocationSet(): boolean {
    return this.createNotificationForm.controls.selectedLocation.touched && this.createNotificationForm.controls.selectedLocation.dirty && this.createNotificationForm.controls.selectedLocation.valid;
  }

  ngOnInit(): void {
    if (this.loadData && this.notificationMetadata.length === 0) {
      this.notificationClient.getNotificationsMetadata(true)
        .then(response => {
          this.notificationMetadata = response.result;
          this.availableNotificationTypes = [...this.notificationMetadata.map(d => { return { value: d.Code, label: d.Name } })];

          this.selectedNotificationType = this.availableNotificationTypes[0].value;
          this.availableTargets = this.notificationMetadata.find(n => n.Code == this.selectedNotificationType).Targets;

          this.availableTargetOptions = [...this.availableTargets.map(d => { return { value: d.Code, label: d.Description } })];
          this.selectedTargetType = this.availableTargetOptions[0].value;

          this.availableProperties = [...this.notificationMetadata.find(n => n.Code == this.selectedNotificationType).Fields];
          this.dynamicFieldsMetadata = [... this.availableProperties.sort((a, b) => a.Row - b.Row)];

          this.rebuildDynamicFormControls();

          this.notificationClient.getTargetRights()
            .then(response => {
              if (response?.result)
                this.targetRights = response.result;
              if (this.targetRights) {
                this.createNotificationForm.controls.selectedCompanyGroup.disable({ emitEvent: false });
                this.createNotificationForm.controls.selectedLocation.disable({ emitEvent: false });

                this.createNotificationForm.patchValue({
                  selectedCompanyGroup: [{ label: this.targetRights.CompanyGroupName, value: this.targetRights.CompanyGroupCode }],
                  selectedLocation: [{ label: this.targetRights.CustomerName, value: this.targetRights.CustomerCode }]
                }, { emitEvent: false });

                this.cdr.detectChanges();
              }
            });
        });
    }

    this.createNotificationForm.controls.selectedLocation!.valueChanges.subscribe(value => {
      if (value)
        this.createNotificationForm.controls.selectedUser.enable();
      else
        this.createNotificationForm.controls.selectedUser.disable();
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.createNotificationForm.controls.selectedUser.disable();
    }, 1000)
  }

  ngOnDestroy(): void {
    this.notificationMetadata = [];
    this.availableNotificationTypes = [];
    this.availableTargets = [];
    this.availableProperties = [];
  }

  onSelectedNotificationTypeChanged() {
    this.availableTargets = this.notificationMetadata.find(n => n.Code == this.selectedNotificationType).Targets;
    this.availableTargetOptions = [...this.availableTargets.map(d => { return { value: d.Code, label: d.Description, disabled: false } })];
    this.availableProperties = [...this.notificationMetadata.find(n => n.Code == this.selectedNotificationType).Fields];
    this.dynamicFieldsMetadata = [... this.availableProperties.sort((a, b) => a.Row - b.Row)];
    this.rebuildDynamicFormControls();
  }

  onSelectedTargetTypeChanged(event) {
    switch (event?.value) {
      case TargetTypesEnum.Location:
        this.createNotificationForm.controls.selectedCompanyGroup.clearValidators();
        this.createNotificationForm.controls.selectedUser.clearValidators();
        if (!this.targetRights) {
          this.createNotificationForm.patchValue({ selectedCompanyGroup: [] });
          this.createNotificationForm.controls.selectedLocation.enable();
        }
        this.createNotificationForm.patchValue({ selectedUser: [] });
        this.createNotificationForm.controls.selectedLocation.addValidators(validateOptionalDropdown(TargetTypesEnum.Location, this.selectedTargetType));
        this.selectedNotificationInstance.Targets.filter(c => c.Code != TargetTypesEnum.Location).forEach(t => t.Selected = false);
        break;
      case TargetTypesEnum.User:
        this.createNotificationForm.controls.selectedCompanyGroup.clearValidators();
        this.createNotificationForm.controls.selectedLocation.clearValidators();
        if (!this.targetRights) {
          this.createNotificationForm.patchValue({ selectedCompanyGroup: [], selectedLocation: [] });
          this.createNotificationForm.controls.selectedLocation.enable();
          this.createNotificationForm.controls.selectedUser.disable();
        }
        else {
          this.createNotificationForm.controls.selectedLocation.disable();
          this.createNotificationForm.controls.selectedUser.enable();
        }

        this.createNotificationForm.controls.selectedUser.addValidators(validateOptionalDropdown(TargetTypesEnum.User, this.selectedTargetType));
        this.selectedNotificationInstance.Targets.filter(c => c.Code != TargetTypesEnum.User).forEach(t => t.Selected = false);

        break;
      case TargetTypesEnum.CompanyGroup:
        this.createNotificationForm.controls.selectedLocation.clearValidators();
        this.createNotificationForm.controls.selectedUser.clearValidators();
        if (!this.targetRights) {
          this.createNotificationForm.patchValue({ selectedLocation: [] });
        }
        this.createNotificationForm.patchValue({ selectedUser: [] });
        this.createNotificationForm.controls.selectedCompanyGroup.addValidators(validateOptionalDropdown(TargetTypesEnum.CompanyGroup, this.selectedTargetType));
        this.selectedNotificationInstance.Targets.filter(c => c.Code != TargetTypesEnum.CompanyGroup).forEach(t => t.Selected = false);
        break;
      default:
        this.createNotificationForm.controls.selectedCompanyGroup.clearValidators();
        this.createNotificationForm.controls.selectedLocation.clearValidators();
        this.createNotificationForm.controls.selectedUser.clearValidators();
        if (!this.targetRights) {
          this.createNotificationForm.patchValue({ selectedCompanyGroup: [], selectedLocation: [] });
        }
        this.createNotificationForm.patchValue({ selectedUser: [] });
        this.selectedNotificationInstance.Targets.filter(c => c.Code != TargetTypesEnum.All).forEach(t => t.Selected = false);
        break;
    }
  }

  rebuildDynamicFormControls(): void {
    const dynamicGroup = this.createNotificationForm.get('dynamicFields') as FormGroup;
    Object.keys(dynamicGroup.controls).forEach(key => dynamicGroup.removeControl(key));

    this.dynamicFieldsMetadata.forEach(field => {
      const validators = [];
      if (field.Mandatory)
        validators.push(Validators.required);

      if (field.MaxLength)
        validators.push(Validators.maxLength(field.MaxLength));

      dynamicGroup.addControl(
        field.Code,
        new FormControl({ value: field.DefaultValue ?? '', disabled: field.Editable === false }, validators)
      );
    });
  }

  searchCompanyGroup(event: any) {
    if (event?.query?.length > 3)
      this.notificationClient.getTargetTypeCollection(TargetTypes.CompanyGroup, event.query)
        .then((response) => {
          this.companyGroupCollection =
            [
              ...response.result.map(r => { return { label: r.Value, value: r.Key }; })
            ];
        });
  }

  searchLocation(event: any, fromSelectUser: boolean = false) {
    if (fromSelectUser && (this.createNotificationForm.controls.selectedLocation.getRawValue())?.length === 1)
      return;
    if (event?.query?.length > 3)
      this.notificationClient.getTargetTypeCollection(TargetTypes.Location, event.query)
        .then((response) => {
          this.locationCollection =
            [
              ...response.result.map(r => { return { label: r.Value, value: r.Key }; })
            ];
        });
  }

  searchUser(event: any) {
    let selectedLocation = (this.createNotificationForm.controls.selectedLocation.getRawValue() as any)[0].value;
    this.createNotificationForm.controls.selectedLocation.getRawValue()
    if (event?.query?.length > 3)
      this.notificationClient.getTargetTypeCollection(TargetTypes.User, event.query, this.selectedNotificationType, selectedLocation)
        .then((response) => {
          this.userCollection =
            [
              ...response.result.map(r => { return { label: r.Value, value: r.Key }; })
            ];
        });
  }

  saveNotification(publishNotification: boolean) {
    let requestBody = this.preparePayload();
    let notificationStatus = publishNotification
      ? NotificationStatuses.Publishing
      : NotificationStatuses.Draft;

    this.notificationClient.submitNotifications([requestBody], notificationStatus)
      .then(
        (response) => {
          const savedNotification = response?.result[0];
          const savedNotificationID = savedNotification.PK_ID;
          if (savedNotificationID) {
            requestBody.Fields.forEach(f => f.NotificationId = savedNotificationID);
            requestBody.CreatedAt = savedNotification.CreatedAt;
            requestBody.ExpiresAt = savedNotification.ExpiresAt;
            requestBody.NotificationStatusId = savedNotification.FK_NotificationStatus;
            requestBody.PK_ID = savedNotification.PK_ID;
            requestBody.NotificationStatusDescription = publishNotification ? 'Publishing' : 'Draft';
            this.notificationCreated.emit(requestBody);
          }
        }
        , (error) => {
          console.error(error)
        });

  }

  private preparePayload() {
    let formData = this.createNotificationForm.getRawValue();
    let requestBody = this.notificationMetadata.find(n => n.Code === this.selectedNotificationType);
    let dynamicFields = (formData.dynamicFields as any);
    if (dynamicFields?.expire_on) {
      requestBody.ExpiresAt = dynamicFields.expire_on;
      toUtcDate(requestBody.ExpiresAt);
    }

    requestBody.Targets.find(t => t.Code === this.selectedTargetType).Selected = true;

    let selectedCode: number[] = [];
    if (formData.selectedCompanyGroup && formData.selectedCompanyGroup.length > 0)
      selectedCode = [...formData.selectedCompanyGroup.map(v => Number(v.value))];
    else if (formData.selectedUser && formData.selectedUser.length > 0)
      selectedCode = [...formData.selectedUser.map(v => Number(v.value))];
    else if (formData.selectedLocation && formData.selectedLocation.length > 0)
      selectedCode = [...formData.selectedLocation.map(v => Number(v.value))];
    else
      selectedCode = [0]; // for target "all"

    requestBody.Targets.find(t => t.Code === this.selectedTargetType).TargetCodes = selectedCode.length > 0 ? [...selectedCode] : [];

    requestBody.Fields.forEach(field => {
      const value = dynamicFields[field.Code];
      if (value) {
        field.Value = value;
      }
    });
    return requestBody;
  }
}

export enum TargetTypesEnum {
  All = 'all',
  User = 'user',
  Location = 'location',
  CompanyGroup = 'company_group',
}

export enum NotificationStatuses {
  Draft = 1,
  Publishing,
  Published,
  Expired,
  Deleted,
}

export function validateOptionalDropdown(controlName: string, selectedTargetType: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;

    switch (selectedTargetType) {
      case TargetTypesEnum.All:
        return null;
      case TargetTypesEnum.Location: {
        if (controlName === TargetTypesEnum.Location) {
          if (value === null || value === undefined || value?.length === 0)
            return { 'validateOptionalDropdown': { mandatory: true } };
          else
            return null;
        }
      }
        break;
      case TargetTypesEnum.User: {
        if (controlName === TargetTypesEnum.User) {
          if (value === null || value === undefined || value?.length === 0)
            return { 'validateOptionalDropdown': { mandatory: true } };
          else
            return null;
        }
      }
        break;
      case TargetTypesEnum.CompanyGroup: {
        if (controlName === TargetTypesEnum.CompanyGroup) {
          if (value === null || value === undefined || value?.length === 0)
            return { 'validateOptionalDropdown': { mandatory: true } };
          else
            return null;
        }
      }
      default:
        return null; // Valid
    };
  }
}

